import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  InputAdornment,
  TextField,
  Typography,
  Menu,
  MenuItem,
} from "@mui/material";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";

export default function AddedBulkCandidates({ processedFiles, isProcessing }) {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  useEffect(() => {
    const storedJobId = localStorage.getItem("bulkUploadJobId");
    if (!storedJobId) {
      navigate("/Manage-Candidates", { replace: true });
    }
  }, [navigate]);

  const columns = [
    {
      field: "sno",
      headerName: "S.No",
      width: 80,
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: "fileName",
      headerName: "File Name",
      flex: 1,
      minWidth: 150,
      sortable: true,
      disableColumnMenu: true,
    },
    {
      field: "fileSize",
      headerName: "File Size",
      width: 120,
      sortable: false,
      disableColumnMenu: true,
      valueFormatter: (params) => {
        const size = params.value;
        return size < 1024 * 1024
          ? `${(size / 1024).toFixed(2)} KB`
          : `${(size / (1024 * 1024)).toFixed(2)} MB`;
      },
    },
    {
      field: "status",
      headerName: "File Status",
      width: 180,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const isSuccess = params.value?.toLowerCase() === "successful";

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.5,
              border: `1px solid ${isSuccess ? "#2E7D32" : "#E65100"}`,
              borderRadius: "16px",
              color: isSuccess ? "#2E7D32" : "#E65100",
              fontWeight: 500,
              fontSize: "0.875rem",
              textTransform: "capitalize",
            }}
          >
            {isSuccess ? (
              <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
            ) : (
              <ErrorOutlineIcon sx={{ fontSize: 18 }} />
            )}
            {params.value}
          </Box>
        );
      },
    },
    {
      field: "message",
      headerName: "Description",
      flex: 1,
      minWidth: 150,
      sortable: false,
      disableColumnMenu: true,
    },
  ];

  const rows = processedFiles.map((file, index) => ({
    id: index + 1,
    sno: index + 1,
    candidateId: file.id,
    ...file,
  }));

  const filteredRows = rows.filter((row) => {
    const matchesSearch = row.fileName
      .toLowerCase()
      .includes(search.toLowerCase());
    const matchesStatus =
      statusFilter === "all" || row.status?.toLowerCase() === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <Box sx={{ backgroundColor: "F9F9F9", padding: "40px 48px" }}>
      <Box sx={{ display: "flex", gap: 2.5, alignItems: "center", mb: 3.5 }}>
        <Button
          variant="contained"
          size="small"
          startIcon={<ArrowBackIosIcon />}
          onClick={() => navigate("/Manage-Candidates")}
          sx={{
            backgroundColor: "#EEEEEE",
            borderRadius: "8px",
            padding: "8px 16px 8px 10px",
            color: "#000",
            "&:hover": {
              backgroundColor: "#e0e0e0",
            },
          }}
        >
          Back
        </Button>

        <Typography sx={{ fontSize: "24px", fontWeight: "400" }}>
          Added Candidates
        </Typography>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
        <TextField
          variant="standard"
          placeholder="Search by file name..."
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        <Box>
          <Button
            variant="contained"
            startIcon={<FilterListIcon />}
            onClick={handleMenuOpen}
            disabled={isProcessing}
          >
            Filter
          </Button>
          <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
            <MenuItem
              disabled={statusFilter === "all"}
              onClick={() => {
                setStatusFilter("all");
                handleMenuClose();
              }}
            >
              All
            </MenuItem>
            <MenuItem
              disabled={statusFilter === "successful"}
              onClick={() => {
                setStatusFilter("successful");
                handleMenuClose();
              }}
            >
              Successful
            </MenuItem>
            <MenuItem
              disabled={statusFilter === "failed"}
              onClick={() => {
                setStatusFilter("failed");
                handleMenuClose();
              }}
            >
              Failed
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      <Box
        sx={{
          height: "calc(100vh - 285px)",
          bgcolor: "white",
          borderRadius: 2,
          mt: 2,
        }}
      >
        <DataGrid
          sx={{
            "& .MuiDataGrid-root": { border: "none" },
            "& .MuiDataGrid-columnHeaders": {
              fontWeight: "bold",
              fontSize: "14px",
              color: "#333",
            },
            "& .MuiDataGrid-row--borderBottom": {
              backgroundColor: "#D9EAFB !important",
            },
            "& .MuiDataGrid-row:nth-of-type(odd)": {
              backgroundColor: "#fafafa",
            },
            "& .MuiDataGrid-row:nth-of-type(even)": {
              backgroundColor: "#ffffff",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: "14px",
              fontWeight: "bold",
              textTransform: "capitalize",
              color: "#2c3e50",
            },
            "& .MuiDataGrid-cell": {
              fontSize: "13px",
              color: "#333",
              alignItems: "center",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "#f5f5f5",
              cursor: "pointer",
            },
          }}
          rows={filteredRows}
          columns={columns.map((col) => ({ ...col, resizable: true }))}
          hideFooter
          onRowClick={(params) => {
            if (params.row.candidateId) {
              navigate(`/Manage-Candidates/${params.row.candidateId}`);
            }
          }}
          getRowClassName={(params) =>
            params.row.candidateId ? "clickable-row" : "non-clickable-row"
          }
        />
      </Box>
    </Box>
  );
}
